import React from "react";
import {XUtils, XViewStatus} from "@michalrakus/x-react-web-lib/XUtils";
import {XOnSaveOrCancelProp} from "@michalrakus/x-react-web-lib/XFormBase";
import {XCustomFilter, XFilterMatchMode} from "@michalrakus/x-react-web-lib/FindParam";
import {
    EnumEnum,
    Rola,
    SluzbaEnum,
    SluzbaRolaPravaKod,
    StatusVSluzbe,
    TypPrav,
    TypPristupuSluzba, XtTemplateType
} from "./common/enums";
import {XUserDePaul} from "./model/user/x-user-de-paul.entity";
import {SluzbaRolaCheckboxMap, SluzbaRolaPrava} from "./model/user/sluzba-rola-prava.entity";
import {XUserSluzbaRola} from "./model/user/x-user-sluzba-rola.entity";
import {Sluzba} from "./model/user/sluzba.entity";
import {
    dateAsUI,
    dateAsYYYY_MM_DD,
    dateFromModel,
    dateFromUI,
    intFromUI,
    XDateScale
} from "@michalrakus/x-react-web-lib/XUtilsConversions";
import {Klient} from "./model/klient/klient.entity";
import {XError, XErrorMap} from "@michalrakus/x-react-web-lib/XErrors";
import {KlientSluzba} from "./model/klient/klient-sluzba.entity";
import {KlientSluzbaZakaz} from "./model/klient/klient-sluzba-zakaz.entity";
import {Chip} from "primereact/chip";
import {XUtilsCommon} from "@michalrakus/x-react-web-lib/XUtilsCommon";
import {XtDocTemplateDePaul} from "./model/doc-templates/xt-doc-template.entity";
import {DataTableFilterMetaData} from "primereact/datatable";
import {VykazStreetwork} from "./model/zapisy/vykaz-streetwork.entity";
import {VykazXUser} from "./model/zapisy/vykaz-x-user.entity";
import {EnvVar, ReactAppZdielanieKlientov} from "./EnvVars";
import {XAutoCompleteInFilterProps} from "@michalrakus/x-react-web-lib/XLazyDataTable";
import {XtDocTemplate} from "@michalrakus/x-react-web-lib/xt-doc-template";
import {TlacitDokumentRequest, TypZakazu} from "./common/klient-api";
import {UtilsCommon} from "./common/UtilsCommon";

export class XUserNotFoundOrDisabledError extends Error {
}

// TODO - presunut do XUtils spolu s funkciou xsUserHasAccess
// access type for statistics/reports
// general rule - if access type level A is required, then the user has to have access type A or higher (user's access type B >= A)
// (e.g. for running statistic is level XsAccessType.usageOnly (1) required, then user has to have level XsAccessType.usageOnly (1) or XsAccessType.full (2))
export enum XsAccessType {
    full = 3,
    usageOnly = 2,  // can create/modify statistic tables / reports but can not create/modify stat fields, group by fields
    runOnly = 1
}

export class Utils {

    // sem si nacachujeme zaznamy SluzbaRolaPrava, aby sme ich nemuseli zakazdym nacitavat
    // zaznamy nacitavame pri spusteni aplikacie
    // poznamka: takyto map mame aj v ZapisBaseForm, ten sa update-uje aj pocas editacie fieldset-u, preto ho tam mame taky separatny
    private static kodSluzbaRolaPravaMap?: Map<string, SluzbaRolaPrava>;

    private static currentSluzba?: Sluzba;
    private static xUserCurrentSluzbaRolaList?: XUserSluzbaRola[]; // sem si nacachujeme vyfiltrovane xUserSluzbaRolaList podla aktualne vykliknutej sluzby

    private static zmenaEntitySluzba: XOnSaveOrCancelProp;

    static getXUserDePaul(): XUserDePaul {
        const xUserDePaul: XUserDePaul = XUtils.getXToken()?.xUser;
        if (!xUserDePaul) {
            throw `Unexpected error: XUtils.getXToken()?.xUser returned undefined/null`;
        }
        return xUserDePaul;
    }

    static isUserAdmin(): boolean {
        return Utils.getXUserDePaul()?.admin;
    }

    static setCurrentSluzba(sluzba: Sluzba) {
        Utils.currentSluzba = sluzba;
        const user: XUserDePaul = Utils.getXUserDePaul();
        // vyfiltrujeme dvojicky sluzba/rola zodpovedajuce aktualnej sluzbe
        Utils.xUserCurrentSluzbaRolaList = user.xUserSluzbaRolaList.filter(
            (xUserSluzbaRola: XUserSluzbaRola) => xUserSluzbaRola.sluzbaRola.sluzba.kod === sluzba.kod);
    }

    static zakladneUdajeZdielane(): boolean {
        return XUtils.getEnvVarValueBase(EnvVar.REACT_APP_ZDIELANIE_KLIENTOV) === ReactAppZdielanieKlientov.ZAKLADNE_UDAJE;
    }

    static klientiNezdielani(): boolean {
        // (zaznam KlientSluzba si zapisuje do sluzba_id konkretnu sluzbu)
        return XUtils.getEnvVarValueBase(EnvVar.REACT_APP_ZDIELANIE_KLIENTOV) === ReactAppZdielanieKlientov.ZAKLADNE_UDAJE
                || XUtils.getEnvVarValueBase(EnvVar.REACT_APP_ZDIELANIE_KLIENTOV) === ReactAppZdielanieKlientov.ZIADNE_UDAJE;
    }

    static getCurrentSluzba(): Sluzba | undefined {
        return Utils.currentSluzba;
    }

    static getCurrentSluzbaId(): number | undefined {
        return Utils.getCurrentSluzba()?.id;
    }

    static isSluzbaNoclaharen(): boolean {
        return Utils.getCurrentSluzba()?.kod === SluzbaEnum.noclaharen;
    }

    static isSluzbaStreetwork(): boolean {
        return Utils.getCurrentSluzba()?.kod === SluzbaEnum.streetwork;
    }

    static isSluzbaLujzaPoradenstvo(): boolean {
        return Utils.getCurrentSluzba()?.kod === SluzbaEnum.lujzaPoradenstvo;
    }

    static isSluzbaVincentPoradenstvo(): boolean {
        return Utils.getCurrentSluzba()?.kod === SluzbaEnum.vincentPoradenstvo;
    }

    static isSluzbaAlzbetaPoradenstvo(): boolean {
        return Utils.getCurrentSluzba()?.kod === SluzbaEnum.alzbetaPoradenstvo;
    }

    static isSluzbaUtulok(): boolean {
        const sluzbaKod: string | undefined = Utils.getCurrentSluzba()?.kod;
        return sluzbaKod === SluzbaEnum.lujzaPoradenstvo || sluzbaKod === SluzbaEnum.vincentPoradenstvo;
    }

    static isSluzbaSocPorad(): boolean {
        const sluzbaEnum: SluzbaEnum | undefined = Utils.getCurrentSluzba()?.kod as SluzbaEnum;
        return Utils.isSluzbaEnumSocPorad(sluzbaEnum);
    }

    // sluzby ktore maju formular SocPorad (vela zaloziek) - zatial provizorne takto
    static isSluzbaEnumSocPorad(sluzbaEnum: SluzbaEnum | undefined): boolean {
        return (sluzbaEnum !== SluzbaEnum.noclaharen && sluzbaEnum !== SluzbaEnum.alzbetaOsetrovna);
    }

    // sluzby, ktore maju zapisy s malym poctom klikaciek (momentalne Alzeba osetrovna a osetrovna v ramci noclaharne)
    static isSluzbaOsetrovna(): boolean {
        const sluzbaEnum: SluzbaEnum | undefined = Utils.getCurrentSluzba()?.kod as SluzbaEnum;
        return (sluzbaEnum === SluzbaEnum.noclaharen || sluzbaEnum === SluzbaEnum.alzbetaOsetrovna);
    }

    static getXUserCurrentSluzbaRolaList(): XUserSluzbaRola[] | undefined {
        return Utils.xUserCurrentSluzbaRolaList;
    }

    static setZmenaEntitySluzba(zmenaEntitySluzba: XOnSaveOrCancelProp) {
        Utils.zmenaEntitySluzba = zmenaEntitySluzba;
    }

    static getZmenaEntitySluzba(): XOnSaveOrCancelProp {
        return Utils.zmenaEntitySluzba;
    }

    // TODO - prehodit do XUtils
    static enumFilter(enumEnumCode: string): XCustomFilter {
        return {where: `[xEnumEnum.code] = '${enumEnumCode}' AND [enabled] = :enabled`, params: {enabled: true}};
    }

    // pre miesto mozneho kontaktu (dennik + entita miesto mozneho kontaktu) pouzivame enum mestskaCast ale bez casti Raca a Petrzalka
    // (na karte klienta ponechavame aj casti Raca a Petrzalka)
    static miestoMoznehoKontaktuMestskaCastFilter(): XCustomFilter {
        return XUtilsCommon.filterAnd(Utils.enumFilter(EnumEnum.mestskaCast), {where: `[code] NOT IN ('Raca', 'Petrzalka')`, params: {}})!;
    }

    static async getSequenceValue(sequenceName: string): Promise<number> {
        const {value} = await XUtils.fetch('x-get-sequence-value', {name: sequenceName});
        return value;
    }

    static klientFilterCurrentSluzba(ostatniKlienti: boolean = false): XCustomFilter {
        return {where: `${ostatniKlienti ? "NOT " : ""}EXISTS (SELECT 1 FROM ${Utils.getSchema()}.klient_sluzba ks WHERE ks.klient_id = [id] AND ks.sluzba_id = :sluzbaId)`, params: {"sluzbaId": Utils.getCurrentSluzbaId() ?? 0}};
    }

    // static klientFilterNoclaharen(): XCustomFilter {
    //     return {where: "[noclaharen] = :noclaharen", params: {"noclaharen": true}};
    // }

    // static klientFilterSluzba(sluzbaEnum: SluzbaEnum): XCustomFilter {
    //     return {where: "[sluzba] IS NULL OR [sluzba.kod] = :sluzbaKod", params: {"sluzbaKod": sluzbaEnum}};
    // }

    // toto treba pouzivat na entitach ktore obsahuju ManyToOne asociaciu sluzba (KlientSluzba, Zmluva a pod.)
    static filterCurrentSluzba(): XCustomFilter {
        return {where: `[sluzba] = ${Utils.getCurrentSluzbaId() ?? 0}`, params: {}};
    }

    static filterSluzba(sluzbaEnum: SluzbaEnum): XCustomFilter {
        return {where: "[sluzba.kod] = :sluzbaKod", params: {"sluzbaKod": sluzbaEnum}};
    }

    /**
     * vrati prava vo forme XViewStatus
     * @param pravoKod
     */
    static userPravoKodViewStatus(pravoKod: SluzbaRolaPravaKod): XViewStatus {
        return Utils.userPravoViewStatus(Utils.getSluzbaRolaPrava(pravoKod));
    }

    /**
     * vrati prava vo forme boolean
     * malo by sa pouzivat pre typPrav = lenCheckbox
     * @param pravoKod
     */
    static userPravoKodBoolean(pravoKod: SluzbaRolaPravaKod): boolean {
        return Utils.userPravoKodViewStatus(pravoKod) === XViewStatus.ReadWrite;
    }

    /**
     * vrati prava vo forme XViewStatus
     * @param sluzbaRolaPrava
     */
    static userPravoViewStatus(sluzbaRolaPrava: SluzbaRolaPrava): XViewStatus {

        const currentSluzba: Sluzba | undefined = Utils.getCurrentSluzba();
        if (!currentSluzba) {
            return XViewStatus.Hidden; // nema vybratu sluzbu v comboboxe v menu
        }

        let pravo: XViewStatus = XViewStatus.Hidden; // default
        const sluzbaRolaMap: SluzbaRolaCheckboxMap | undefined = sluzbaRolaPrava.prava[currentSluzba.kod];
        if (sluzbaRolaMap) {
            const xUserCurrentSluzbaRolaList: XUserSluzbaRola[] | undefined = Utils.getXUserCurrentSluzbaRolaList();
            if (!xUserCurrentSluzbaRolaList) {
                return XViewStatus.Hidden; // nemalo by vobec nastat, ak nemame vybratu sluzbu, uz to vyskoci vyssie
            }
            if (sluzbaRolaPrava.typPrav.code === TypPrav.lenCheckbox) {
                // ak user ma nejaku dvojicku sluzba/rola, ktora je zaskrtnuta tak mame readWrite prava
                if (xUserCurrentSluzbaRolaList.some((xUserSluzbaRola: XUserSluzbaRola) => sluzbaRolaMap[xUserSluzbaRola.sluzbaRola.id])) {
                    pravo = XViewStatus.ReadWrite;
                }
            }
            else if (sluzbaRolaPrava.typPrav.code === TypPrav.citanieZapis) {
                // najdeme co najvyssie pravo
                for (const xUserSluzbaRola of xUserCurrentSluzbaRolaList) {
                    const viewStatus: XViewStatus | undefined = sluzbaRolaMap[xUserSluzbaRola.sluzbaRola.id] as XViewStatus | undefined;
                    if (viewStatus !== undefined) {
                        if (viewStatus === XViewStatus.ReadWrite) {
                            // najvyssia uroven
                            pravo = XViewStatus.ReadWrite;
                        }
                        else if (viewStatus === XViewStatus.ReadOnly) {
                            // ak mame najnizsiu uroven, tak ju zvysime
                            if (pravo === XViewStatus.Hidden) {
                                pravo = XViewStatus.ReadOnly;
                            }
                        }
                    }
                }
            }
            else {
                throw 'Unexpected value of sluzbaRolaPrava.typPrav.code = ' + sluzbaRolaPrava.typPrav.code;
            }
        }
        return pravo;
    }

    /**
     * vrati prava vo forme boolean
     * malo by sa pouzivat pre typPrav = lenCheckbox
     * pre typPrav = citanieZapis robime transformaciu XViewStatus.ReadWrite -> true, XViewStatus.ReadOnly a XViewStatus.Hidden -> false
     * @param sluzbaRolaPrava
     */
    static userPravoBoolean(sluzbaRolaPrava: SluzbaRolaPrava): boolean {
        return Utils.userPravoViewStatus(sluzbaRolaPrava) === XViewStatus.ReadWrite;
    }

    /**
     * vrati true ak ma aktualny user v dropdowne pre vyber sluzby vybratu danu sluzbu "sluzbaEnum"
     * a zaroven je bud admin (ma pravo na vsetko) alebo ak nie je admin tak ma priradenu nejaku dvojicku (sluzba, rola)
     * kde sluzba je "sluzbaEnum" a rola je jedna z roli zadanych v array "rolaCode" (napr. "veduci", "socialnyPracovnik", ...)
     *
     * @param sluzbaEnum
     * @param rolaCode
     */
    static userMaSluzbuRolu(sluzbaEnum: SluzbaEnum, ...rolaCode: Rola[]): boolean {

        const currentSluzba: Sluzba | undefined = Utils.getCurrentSluzba();
        if (!currentSluzba) {
            return false; // nema vybratu sluzbu v comboboxe v menu
        }

        if (currentSluzba.kod !== sluzbaEnum) {
            return false; // nie je vybrata spravna sluzba
        }

        let maSluzbuRolu: boolean = false; // default
        const user: XUserDePaul = Utils.getXUserDePaul();
        if (user.admin) {
            maSluzbuRolu = true;
        }
        else {
            const xUserCurrentSluzbaRolaList: XUserSluzbaRola[] | undefined = Utils.getXUserCurrentSluzbaRolaList();
            if (!xUserCurrentSluzbaRolaList) {
                return false; // nemalo by vobec nastat, ak nemame vybratu sluzbu, uz to vyskoci vyssie
            }
            maSluzbuRolu = xUserCurrentSluzbaRolaList.some(
                (xUserSluzbaRola: XUserSluzbaRola) => rolaCode.includes(xUserSluzbaRola.sluzbaRola.rola.code as Rola));
        }

        return maSluzbuRolu;
    }

    /**
     * vrati objekt pre property autoComplete v XLazyColumn - autoComplete ponuka userov ktori maju prava na aktualnu sluzbu
     * @param pravoKod
     */
    static userColumnAutoComplete(assocFieldToXUser: string): XAutoCompleteInFilterProps {
        return {
            assocField: assocFieldToXUser, field: "name",
            filter: XUtilsCommon.filterAnd(
                {where: "[xUserSluzbaRolaList.sluzbaRola.sluzba] = :sluzbaId", params: {"sluzbaId": Utils.getCurrentSluzbaId() ?? 0}},
                {where: "[nezobrazovatVPonukePracovnikov] = :nezobrazovatVPonukePracovnikov", params: {nezobrazovatVPonukePracovnikov: false}}),
            lazyLoadMaxRows: 15, scrollHeight: "25rem"
        };
    }

    /**
     * vrati datum zohladnujuci "trochu odlisny" datum noclaharne
     */
    static today(): Date {
        return Utils.isSluzbaNoclaharen() ? Utils.todayNoclaharen() : XUtilsCommon.today();
    }

    /**
     * vrati logicky datum - ak mame aktualny cas od 0:00 do 11:00 (11 je parameter n_denPredlzenyOxHodin) tak vrati datum predchadzajuceho dna
     */
    static todayNoclaharen(): Date {
        // TODO - tahat z parametrov, ked budu nacachovane
        //const denPredlzenyOxHodin: number = Utils.getXParamValueAsInt(Param.n_denPredlzenyOxHodin);
        const denPredlzenyOxHodin: number = 9; // zatial natvrdo
        const today = new Date();
        // ked pouzivam kombinaciu today.setUTCHours + today.getHours() tak to funguje tak ako ma, minimalne pre letny cas
        //today.setUTCHours(today.getHours() - denPredlzenyOxHodin); // posunieme cas dozadu o x hodin (aktualny den klesne o 1 ak sme v case 0:00 - 11:00)
        today.setHours(today.getHours() - denPredlzenyOxHodin); // posunieme cas dozadu o x hodin (aktualny den klesne o 1 ak sme v case 0:00 - 11:00)

        // vynulujeme casovu zlozku
        // poznamka: Date vzdy obsahuje aj casovu zlozku. Nase konverzne funkcie dateFromModel a dateFromUI pouzivaju konverziu new Date('YYYY-MM-DD')
        // a tato konverzia vytvara datum s GMT/UTC/Z casom 00:00:00 (stredoeuropsky 00:01:00 - akokeby sme zadavali new Date('YYYY-MM-DDT00:00:00Z'))
        //today.setHours(0, 0, 0, 0); // nastavi cas 00:00:00 v aktualnej timezone (stredoeuropsky 00:00:00, GMT 23:00:00)
                                    // - potom nam nefunguje porovnavanie s datumami vytvorenymi cez funkcie dateFromModel a dateFromUI
        //today.setUTCHours(0, 0, 0, 0);
        return new Date(dateAsYYYY_MM_DD(today));
    }

    // static klientMaZakazUbytovatSa(klient: Klient, today: Date): boolean {
    //     return klient.klientZakazUbytovatList.some((value: KlientZakazUbytovat) => dateFromModel(value.datumOd)! <= today && today <= dateFromModel(value.datumDo)!);
    // }

    /**
     * vrati typ zakazu ku dnu today
     * @param klientSluzba
     * @param today
     */
    static klientSluzbaZistiTypZakazu(klientSluzba: KlientSluzba, today: Date): TypZakazu {
        let typZakazu: TypZakazu = TypZakazu.bezZakazu;
        let klientSluzbaZakazList: KlientSluzbaZakaz[] = klientSluzba.klientSluzbaZakazList.filter((value: KlientSluzbaZakaz) => dateFromModel(value.datumOd)! <= today && today <= dateFromModel(value.datumDo)!);
        if (klientSluzbaZakazList.length > 0) {
            if (klientSluzbaZakazList.length > 1) {
                // vezmeme posledny (najnovsi, s najvyssim id), nemame validaciu na prekryvy
                klientSluzbaZakazList = XUtilsCommon.arraySort(klientSluzbaZakazList, "id");
            }
            const klientSluzbaZakaz: KlientSluzbaZakaz = klientSluzbaZakazList[klientSluzbaZakazList.length - 1];
            typZakazu = UtilsCommon.klientSluzbaZakazZistiTypZakazu(klientSluzbaZakaz);
        }
        return typZakazu;
    }

    /**
     * vrati class name pre dany typ zakazu
     * @param klientSluzba
     * @param today
     */
    static klientSluzbaZistiTypZakazuClassName(typZakazu: TypZakazu): string | undefined {
        let typZakazuClassName: string | undefined = undefined;
        switch (typZakazu) {
            case TypZakazu.bezZakazu: typZakazuClassName = undefined; break;
            case TypZakazu.kratkodoby: typZakazuClassName = 'klient-sluzba-zakaz-kratkodoby'; break;
            case TypZakazu.dlhodoby: typZakazuClassName = 'klient-sluzba-zakaz-dlhodoby'; break;
            case TypZakazu.trvaly: typZakazuClassName = 'klient-sluzba-zakaz-trvaly'; break;
            default:
                throw 'Neznamy typ zakazu';
        }
        return typZakazuClassName;
    }

    static klientSluzbaZosnuly(klientSluzba: KlientSluzba): boolean {
        return klientSluzba.statusVSluzbe?.code === StatusVSluzbe.zosnuly;
    }

    static klientSluzbaNeaktivnyOrNieJeKlient(klientSluzba: KlientSluzba): boolean {
        return klientSluzba.statusVSluzbe?.code === StatusVSluzbe.neaktivnyKlient || klientSluzba.statusVSluzbe?.code === StatusVSluzbe.nieJeKlient;
    }

    // pomocna metodka
    static klientCreateLabel(label: string, klient: Klient | null | undefined): string {
        if (klient) {
            if (klient.meno) {
                label += " " + klient.meno;
            }
            if (klient.priezvisko) {
                label += " " + klient.priezvisko;
            }
            if (klient.prezyvka) {
                label += " " + klient.prezyvka;
            }
        }
        return label;
    }

    // pomocna metodka
    static klientCreateIDInfo(klient: Klient): string {
        return `${klient.meno} ${klient.priezvisko} ${dateAsUI(dateFromModel(klient.datumNarodenia), klient.datumNarodeniaIbaRok ? XDateScale.Year : XDateScale.Date)}`;
    }

    static createChips(labelList: string[]): JSX.Element[] {
        return labelList.map((value: string) => <Chip label={value} className="m-1"/>);
    }

    static klientSluzbaFieldListForAutoComplete(poznamka?: boolean, poznamkaOsetrovna?: boolean): [string[], string[], number] {
        let fieldList: string[] = ["klient.meno", "klient.priezvisko", "klient.prezyvka", "RČ:klient.rodneCislo", /*"klient.pohlavie.name",*/ "klient.datumNarodenia", "klient.ulica", "klient.cisloDomu", "klient.psc", "klient.obecText", "klient.obec.nazov", "klient.stat.nazov"];
        let fieldListNotDisplayed: string[] = ["klientSluzbaZakazList.id", "statusVSluzbe.code"]; // tieto potrebujeme tiez nacitat (koli farbickovaniu) ale ak by sme ich dali do fieldList, tak by sa zobrazovali v dropdown paneli a inpute
        let minLength: number;
        if (Utils.isSluzbaNoclaharen()) {
            // v noclaharni chceme aj cislo vreca, dame ho na zaciatok
            // (poznamka: cisloVreca by sme mohli dat aj do inych sluzieb, je nevyplnene, tak by ho nebolo vidno... potom by sme mohli mat 1 zoznam)
            fieldList.unshift("V:cisloVreca");
            minLength = 2; // koli jednocifernym cislam vreci - napr. V5
        }
        else {
            minLength = 3;
        }
        if (Utils.isSluzbaLujzaPoradenstvo()) {
            fieldList.push("lujzaPoradProjekt.name");
        }
        else if (Utils.isSluzbaVincentPoradenstvo()) {
            fieldList.push("vincentPoradProjekt.name");
        }
        //fieldList.push("ID:klient.id");
        // na koniec
        if (poznamka) {
            fieldList.push("poznamka");
        }
        if (poznamkaOsetrovna) {
            fieldList.push("poznamkaOsetrovna");
        }
        return [fieldList, fieldListNotDisplayed, minLength];
    }

    // helper
    static async klientSluzbaFromFilterReread(filterItemKlientSluzba: DataTableFilterMetaData | undefined, poznamka: boolean, poznamkaOsetrovna: boolean): Promise<KlientSluzba | null> {
        let klientSluzbaFromDB: KlientSluzba | null = null;
        if (filterItemKlientSluzba) {
            if (filterItemKlientSluzba.matchMode as XFilterMatchMode === XFilterMatchMode.X_FILTER_ELEMENT && typeof filterItemKlientSluzba.value === 'object' && filterItemKlientSluzba.value !== null) {
                const klientSluzbaFromFilter: KlientSluzba = filterItemKlientSluzba.value;
                // mame klientSluzba z filtra ale rereadneme ho z DB - dovod je aby sme mali co najnovsie data
                // (inac hrozi ze uzivatel uvidi stare neaktualne data a save padne na optimistickom locku (stara hodnota version))
                // poznamka: novsiu verziu klienta (voci instancii ulozenej vo filtri) si vie uzivatel vytvorit tak ze zmodifikuje poznamku (a sejvne zapis)
                // nacitavame vsetky fieldy ktore sa nacitavaju ked sa nacitavaju zaznamy KlientSluzba pre KlientSluzbaAutoComplete (pouzivany na ZapisForm)
                // dolezite su pre nas asociacie (fieldy na danej entite sa nacitavaju vsetky)
                const [fieldList, fieldListNotDisplayed, minLength]: [string[], string[], number] = Utils.klientSluzbaFieldListForAutoComplete(poznamka, poznamkaOsetrovna);
                let fields: string[] = [...fieldList, ...fieldListNotDisplayed];
                // zbavime sa prefixov - napr. "RČ:klient.rodneCislo" -> pouzijeme "klient.rodneCislo"
                fields = fields.map((field: string) => {
                    const [prefix, fieldOnly]: [string | null, string] = XUtilsCommon.getPrefixAndField(field);
                    return fieldOnly;
                })
                klientSluzbaFromDB = await XUtils.fetchById("KlientSluzba", fields, klientSluzbaFromFilter.id);
            }
        }
        return klientSluzbaFromDB;
    }

    // helper - vrati pracovnika na predplnenie - t.j. aktualneho pracovnika ak sa tento nachadza na denniku
    static streetworkZapisPracovnikForInit(vykazStreetwork: VykazStreetwork): XUserDePaul | null {
        const currentUser: XUserDePaul = Utils.getXUserDePaul();
        return XUtilsCommon.arrayIncludes<XUserDePaul>(vykazStreetwork.vykazXUserList.map((vykazXUser: VykazXUser) => vykazXUser.xUser), currentUser, 'id') ? currentUser : null;
    }

    static async fetchAndSetKodSluzbaRolaPravaMap(): Promise<any> {
        if (Utils.kodSluzbaRolaPravaMap === undefined) {
            const sluzbaRolaPravaList: SluzbaRolaPrava[] = await XUtils.fetchRows('SluzbaRolaPrava', XUtilsCommon.createCustomFilter(`[kod] IS NOT NULL`), undefined, ['typPrav.code']);
            Utils.kodSluzbaRolaPravaMap = XUtilsCommon.arrayCreateMap<string, SluzbaRolaPrava>(sluzbaRolaPravaList, 'kod');
        }
    }

    static getSluzbaRolaPrava(pravoKod: SluzbaRolaPravaKod): SluzbaRolaPrava {
        if (Utils.kodSluzbaRolaPravaMap === undefined) {
            throw `Unexpected error - Utils.kodSluzbaRolaPravaMap is undefined`;
        }

        const sluzbaRolaPrava: SluzbaRolaPrava | undefined = Utils.kodSluzbaRolaPravaMap.get(pravoKod);
        if (sluzbaRolaPrava === undefined) {
            throw `Unexpected error - row SluzbaRolaPrava not found for kod = ${pravoKod}`;
        }

        return sluzbaRolaPrava;
    }

    // pomocna metodka na zjednotenie kodu, docasne, kym nevyriesime vytvaranie fileName na backend-e
    static async klientSluzbaTlacitDokument(docTemplate: XtDocTemplate, tlacitZmluvyRequest: TlacitDokumentRequest, cisloZmluvy: string, klientSluzba: KlientSluzba) {
        // TODO - fileNameTemplate by sa mal riesit na backend-e (z dat nacitanych na backend-e) a z backend-u spolu so suborom by mal prist aj fileName ale je to technicky zlozite
        const fileNameTemplate: string = docTemplate.fileNameTemplate ?? docTemplate.templateXFile.name;
        const cisloZmluvyFile: string = cisloZmluvy.replaceAll('/', '-');
        // pre placeholdery dame klienta (atributy meno, priezvisko, ...) a pridame cisloZmluvy a cisloVreca koli noclaharni
        const row: any = {...klientSluzba.klient, cisloZmluvy: cisloZmluvyFile, cisloVreca: klientSluzba.cisloVreca};
        const fileName = Utils.resolvePlaceholders(fileNameTemplate, row);
        await XUtils.downloadFile('klient-tlacit-dokument', tlacitZmluvyRequest, fileName);
    }

    // vrati dostupne templaty pre danu entitu a pre aktualnu sluzbu
    static async fetchXtDocTemplateList(entity: string): Promise<XtDocTemplateDePaul[]> {
        const customFilter: XCustomFilter = {where: `[entity] = '${entity}' AND [availableInForms] = TRUE`
                + ` AND ([typPristupuSluzba.code] = '${TypPristupuSluzba.vsetkySluzby}'`
                + ` OR ([typPristupuSluzba.code] = '${TypPristupuSluzba.zadaneSluzby}'`
                + ` AND EXISTS (SELECT 1 FROM ${Utils.getSchema()}.xt_doc_template_sluzba ts WHERE ts.xt_doc_template_id = [id] AND ts.sluzba_id = ${Utils.getCurrentSluzbaId() ?? 0})))`, params: {}};
        return XUtils.fetchRows('XtDocTemplate', customFilter, "label", ["templateXFile.name"]);
    }

    // helper
    // TODO - pouzit v fetchXtDocTemplateList
    static docTemplateFilter(entity: string): XCustomFilter {
       return {where: `[entity] = '${entity}'`  // zatial natvrdo
                + ` AND [templateType.code] = '${XtTemplateType.docx}'`        // excel by trebalo doimplementovat na backend-e
                + ` AND ([typPristupuSluzba.code] = '${TypPristupuSluzba.vsetkySluzby}'`
                + ` OR ([typPristupuSluzba.code] = '${TypPristupuSluzba.zadaneSluzby}'`
                + ` AND EXISTS (SELECT 1 FROM ${Utils.getSchema()}.xt_doc_template_sluzba ts WHERE ts.xt_doc_template_id = [id] AND ts.sluzba_id = ${Utils.getCurrentSluzbaId() ?? 0})))`, params: {}};
    }

    // zatial takto natvrdo
    private static schema: string = "depaul";

    static getSchema(): string {
        return Utils.schema;
    }

    // zatial sem, do buducna asi do conversions?
    static stringFieldAsDBNormalised(fieldOrValue: string): string {
        return `trim(lower(${Utils.getSchema()}.unaccent(coalesce(${fieldOrValue}, '__x__'))))`;
    }

    // TODO - presunut do XUtils
    // api pre pristupove prava modulu statistics/reports
    // ked sa statistics/reports presunu do lib-ky, treba mat 2 funkcie - jednu v XUtils a druhu v Utils
    // a tu v Utils treba poslat pri inicializacii app-ky do XUtils (zapisat do static membra) aby ta v XUtils mohla zavolat tu v Utils
    static xsUserHasAccess(xsAccessType: XsAccessType): boolean {
        let userXsAccessType: XsAccessType | undefined = undefined;
        if (Utils.isUserAdmin()) {
            userXsAccessType = XsAccessType.full;
        }
        else if (Utils.userPravoKodBoolean(SluzbaRolaPravaKod.statistikyPouzivanie)) {
            userXsAccessType = XsAccessType.usageOnly;
        }
        else if (Utils.userPravoKodBoolean(SluzbaRolaPravaKod.statistikyLenSpustanie)) {
            userXsAccessType = XsAccessType.runOnly;
        }
        return userXsAccessType !== undefined && userXsAccessType >= xsAccessType; // user has required access type or higher
    }

    // TODO - presunut do XUtils
    static async getXParamValueAsInt(paramCode: string): Promise<number> {
        const paramValue: string = await Utils.getXParamValue(paramCode);
        const paramValueInt: number | null | undefined = intFromUI(paramValue);
        if (paramValueInt === null || paramValueInt === undefined) {
            throw `Param ${paramCode}: could not convert param value ${paramValue} to int.`;
        }
        return paramValueInt;
    }

    // TODO - presunut do XUtils
    static async getXParamValueAsDate(paramCode: string): Promise<Date> {
        const paramValue: string = await Utils.getXParamValue(paramCode);
        const paramValueDate: Date | null | undefined = dateFromUI(paramValue);
        if (paramValueDate === null || paramValueDate === undefined) {
            throw `Param ${paramCode}: could not convert param value ${paramValue} to date.`;
        }
        return paramValueDate;
    }

    // TODO - presunut do XUtils
    static async getXParamValue(paramCode: string): Promise<string> {
        const paramValue: string | null = await XUtils.fetchString('x-get-param-value', {code: paramCode});
        if (paramValue === null || paramValue === "") {
            throw `Param ${paramCode}: param value is empty.`;
        }
        return paramValue;
    }

    // TODO - presunut do XUtils
    static getError(errorMap: XErrorMap, field: string): string | undefined {
        const error: XError = errorMap[field];
        return error ? XUtils.getErrorMessage(error) : undefined;
    }

    // TODO - presunut do XUtilsCommon
    // very simple resolving of placeholders
    // e.g. for value "Client {name} {surname} has contract {contractNumber}" the placeholders will be replaced with values from "row"
    // very simplified copy from XtDocTemplatesService (replacing placeholders in excel templates)
    static resolvePlaceholders(value: string, row: any): string {

        const placeholderList: string[] = Utils.findPlaceholders(value);
        for (const placeholder of placeholderList) {
            const field: string = placeholder.substring(1, placeholder.length - 1); // placeholder (field) can be also path
            const fieldValue: any = XUtilsCommon.getValueOrValueListByPath(row, field);
            const fieldValueString: string = (fieldValue !== null && fieldValue !== undefined) ? fieldValue.toString() : "";
            value = value.replaceAll(placeholder, fieldValueString);
        }
        return value;
    }

    static findPlaceholders(cellValue: string): string[] {
        return cellValue.match(/{[a-zA-Z0-9_.:(),\s]+}/g) ?? [];
    }
}
